import { yupResolver } from '@hookform/resolvers/yup'
import React, { useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import Button from '../../../../components/Button'
import { Container, ContentCenter, Form, Row } from '../../../../components/commons'
import Input from '../../../../components/Input'
import Modal from '../../../../components/Modals/Modal'
import useToast from '../../../../hooks/useToast'
import { api } from '../../../../services/api'

import { UserRegisterForm, registerSchema, updateSchema } from '../../../../validators/auth.schemas'
import ImageInput from '../../../../components/Input/ImageInput'
import SelectInput from '../../../../components/Input/SelectInput'
import states from '../../../../utils/states'
import CheckboxInput from '../../../../components/Input/CheckboxInput'
import { masks } from '../../../../utils/masks'

export interface CreateProps {
  reload: () => void
}

export interface ModalHandle {
  openFormModal: (initialData?: UserRegisterForm) => void
}

type ModalRef = React.ElementRef<typeof Modal>

const CreatePlan: React.ForwardRefRenderFunction<ModalHandle, CreateProps> = ({ reload }, ref) => {
  const [loading, setLoading] = useState(false)

  const [initialData, setInitialData] = useState<UserRegisterForm>({
    name: '',
    email: '',
    document: '',
    state: '',
    manager: false,
    city: '',
    portariaNomeacao: '',
    vigenciaNomeacao: '',
    password: '',
    confirmPassword: '',
    role: 'user',
  })

  const toast = useToast()

  const modalRef = useRef<ModalRef>(null)

  const handleOpenModalCreate = () => {
    modalRef.current?.openModal()
  }

  const {
    setValue,
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<UserRegisterForm>({
    defaultValues: initialData,
    resolver: yupResolver(initialData?._id ? updateSchema : registerSchema),
  })

  const state = watch('state')
  const gestor = watch('manager')

  // Opções de perfis disponíveis
  const roleOptions = [
    { label: 'Administrador', value: 'admin' },
    { label: 'Gerente', value: 'manager' },
    { label: 'Usuário', value: 'user' },
    { label: 'Visualizador', value: 'viewer' },
    { label: 'Digitalizador', value: 'digitizer' },
  ]

  const handleSubmitLogin = async (formData: UserRegisterForm) => {
    try {
      setLoading(true)

      let initialId = initialData?._id

      if (initialData?._id) {
        await api.put(`/users/${initialData?._id}`, {
          ...formData,
          email: undefined,
          photo: undefined,
          confirmPassword: undefined,
          document: masks.unmask(formData?.document),
          password: formData?.password === '' ? undefined : formData?.password,
        })
      } else {
        const { data: responseId } = await api.post('/users/', {
          ...formData,
          document: masks.unmask(formData?.document),
          photo: undefined,
          confirmPassword: undefined,
        })

        initialId = responseId?.data._id
      }

      if (typeof formData.photo === 'object') {
        let formDataUpload: FormData = new FormData()
        formDataUpload.append('photo', formData.photo)
        try {
          await api.put(`/users/${initialId}/upload`, formDataUpload)
        } catch (error) {
          toast.errorToast('Erro ao enviar a foto perfil !')
        }
      }

      modalRef.current?.closeModal()
      reload()
      reset()

      if (initialData?._id) {
        toast.successToast('Usuário criado com sucesso !')
      } else {
        toast.successToast('Usuário Atualizado com sucesso !')
      }
    } catch (error) {
      toast.errorToast('Erro ao criar usuário !')
    } finally {
      setLoading(false)
    }
  }

  React.useImperativeHandle(ref, () => ({
    openFormModal(initialData) {
      reset()
      if (initialData) {
        setInitialData(initialData)
        setValue('name', initialData.name)
        setValue('email', initialData.email)
        setValue('photo', initialData.photo)
        setValue('document', masks.document(initialData.document))
        setValue('state', initialData.state)
        setValue('city', initialData.city)
        setValue('manager', initialData.manager)
        setValue('portariaNomeacao', initialData.portariaNomeacao)
        setValue('vigenciaNomeacao', initialData.vigenciaNomeacao)
        setValue('role', initialData.role)
      } else {
        setInitialData({
          name: '',
          email: '',
          document: '',
          state: '',
          manager: false,
          portariaNomeacao: '',
          vigenciaNomeacao: '',
          city: '',
          password: '',
          confirmPassword: '',
          role: 'user',
        })
        reset()
      }
      handleOpenModalCreate()
    },
  }))

  return (
    <>
      <Modal title='Cadastrar Novo Usuário' ref={modalRef}>
        <Container>
          <ContentCenter>
            <ImageInput borderRadius={100} control={control} name='photo' />
          </ContentCenter>

          <Form onSubmit={handleSubmit(handleSubmitLogin)}>
            <Input
              label='Nome Completo'
              placeholder='Digite seu nome'
              error={errors?.name}
              control={control}
              type='text'
              name='name'
            />
            <Input
              label='CPF'
              placeholder='Digite seu CPF'
              error={errors?.document}
              control={control}
              type='text'
              mask={'cpf'}
              name='document'
            />
            <Row style={{ gap: 15 }}>
              <SelectInput
                label='Estado'
                placeholder='Digite seu estado'
                error={errors?.state}
                control={control}
                options={states.map((state: any) => ({
                  label: state.nome,
                  value: state.sigla,
                }))}
                name='state'
              />
              <SelectInput
                label='Cidade'
                placeholder='Digite sua cidade'
                error={errors?.city}
                control={control}
                options={
                  state
                    ? states
                        .find((stateFound) => stateFound.sigla === state)
                        ?.cidades.map((cid) => ({
                          label: cid,
                          value: cid,
                        }))
                    : []
                }
                //label='E-mail'
                name='city'
              />
            </Row>
            <Input
              placeholder='Digite seu email'
              error={errors?.email}
              control={control}
              label='E-mail'
              disabled={!!initialData?._id}
              type='email'
              name='email'
            />
            <SelectInput
              label='Perfil'
              placeholder='Selecione o perfil do usuário'
              error={errors?.role}
              control={control}
              options={roleOptions}
              name='role'
            />
            <Input
              placeholder='Digite sua senha'
              error={errors?.password}
              control={control}
              label='Senha'
              type='password'
              name='password'
            />
            {!initialData?._id && (
              <Input
                placeholder='Confirme sua senha'
                error={errors?.confirmPassword}
                control={control}
                label='Confirme sua senha'
                type='password'
                name='confirmPassword'
              />
            )}

            <CheckboxInput type='checkbox' name='manager' control={control} label='É Gestor ?' />
            {gestor && (
              <Row style={{ gap: 15 }}>
                <Input
                  label='Portaria de Nomeação'
                  placeholder='Digite a Portaria de Nomeação'
                  error={errors?.portariaNomeacao}
                  control={control}
                  type='text'
                  name='portariaNomeacao'
                />
                <Input
                  label='Vigência de Nomeação'
                  placeholder='Digite sua Vigência de Nomeação'
                  error={errors?.vigenciaNomeacao}
                  control={control}
                  type='text'
                  //label='E-mail'
                  name='vigenciaNomeacao'
                />
              </Row>
            )}

            <Button
              label='Salvar'
              loading={loading}
              type='submit'
              variantType='block'
              color='primary'
            />
          </Form>
        </Container>
      </Modal>
    </>
  )
}

export default React.forwardRef(CreatePlan)
